/**
 * OverviewSurface — the property daily-triage home (reskin of OverviewPage).
 *
 * Renders the design handoff Overview layout wired to REAL data via
 * useOverviewData + the existing model builders (buildGuestCommandModel,
 * buildReadinessItems, buildOverviewActions, buildReputationNarrative):
 * guest hero, "what guests see" (the real backend display SVG via
 * OverviewDisplayPreview — no fabricated Wi-Fi/door codes), commercial KPIs with
 * real sparklines from performanceSummary.trend, a prioritised action rail,
 * reputation mini, stay-readiness checklist, stay-orchestration timeline, and a
 * recent-activity feed.
 *
 * Honesty per the reconciliation: the action rail is READ + CTA deep-links only
 * — no inline resolve/snooze, which would need the (net-new) durable Tasks store
 * rather than sessionStorage.
 */

import { useMemo } from "react"
import { useNavigate } from "react-router"
import { ArrowRight, ChevronRight, Sparkles, Check, AlertTriangle, Star, TrendingUp, TrendingDown } from "lucide-react"
import { usePropertyContext } from "@/lib/usePropertyContext"
import { usePlanContext } from "@/lib/usePlanContext"
import { useActivationEvent } from "@/lib/useActivationEvent"
import { cn } from "@/lib/utils"
import type { PerformanceKpi } from "@/lib/api"
import { FeedbackTrigger } from "@/components/feedback"
import { useOverviewData } from "./useOverviewData"
import { OverviewDisplayPreview } from "./OverviewDisplayPreview"
import { formatRelativeTime } from "./helpers"
import {
  buildGuestCommandModel,
  buildReadinessItems,
  buildOverviewActions,
  buildReputationNarrative,
  type ReadinessTone,
  type ActionTone,
} from "./overview-model"

const eyebrow = "text-[10.5px] font-semibold uppercase tracking-[0.07em] text-muted-foreground"

const ACTION_TONE: Record<ActionTone, { chip: string; text: string }> = {
  critical: { chip: "bg-rose-soft", text: "text-rose-strong" },
  warning: { chip: "bg-amber-soft", text: "text-amber-strong" },
  opportunity: { chip: "bg-brand-soft", text: "text-brand-strong" },
}

const READINESS_TONE: Record<ReadinessTone, { ic: string; bg: string; state: string; ok: boolean }> = {
  ready: { ic: "text-brand-strong", bg: "bg-brand-soft", state: "Ready", ok: true },
  pending: { ic: "text-muted-foreground", bg: "bg-line-soft", state: "Checking", ok: true },
  warning: { ic: "text-amber-strong", bg: "bg-amber-soft", state: "Review", ok: false },
  critical: { ic: "text-rose-strong", bg: "bg-rose-soft", state: "Fix", ok: false },
}

function surfaceRoute(surface: string, propertyId: string): string {
  const base = `/properties/${propertyId}`
  switch (surface) {
    case "guidebook": return `${base}/guest-experience`
    case "display-behaviour": return `${base}/guest-experience/display`
    case "places": return `${base}/guest-experience/places`
    case "house-rules": return `${base}/guest-experience/rules`
    case "reviews": return `${base}/reviews`
    case "performance": return `${base}/performance`
    default: return `${base}/settings`
  }
}

function fmtKpi(kpi: PerformanceKpi | undefined, kind: "currency" | "percent" | "plain"): string {
  if (!kpi || kpi.value == null) return "—"
  if (kind === "currency") {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: kpi.currency || "EUR", maximumFractionDigits: 0 }).format(kpi.value)
  }
  if (kind === "percent") {
    // worker.js sends occupancy as an already-scaled 0–100 percentage
    return `${Math.round(kpi.value)}%`
  }
  return String(Math.round(kpi.value))
}

function Sparkline({ series }: { series: number[] }) {
  if (series.length < 2) return null
  const w = 100, h = 32, p = 4
  const min = Math.min(...series), max = Math.max(...series), span = max - min || 1
  const step = (w - p * 2) / (series.length - 1)
  const points = series
    .map((v, i) => `${(p + i * step).toFixed(1)},${(h - p - ((v - min) / span) * (h - p * 2)).toFixed(1)}`)
    .join(" ")
  return (
    <svg width="100" height="32" viewBox="0 0 100 32" fill="none" className="flex-none">
      <polyline points={points} fill="none" stroke="var(--sf-brand)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

export function OverviewSurface() {
  const { propertyId, property, isDemo } = usePropertyContext()
  const { plan } = usePlanContext()
  const navigate = useNavigate()
  const data = useOverviewData()
  // Onboarding activation — the checklist's "View your dashboard" step depends on this event.
  useActivationEvent(propertyId, "overview_viewed")

  const model = useMemo(() => {
    if (!data.overview) return null
    const guest = buildGuestCommandModel(data.overview, property?.name || "Your property")
    const readiness = buildReadinessItems({
      overview: data.overview,
      guest,
      propertySettings: data.propertySettings,
      propertySettingsStatus: data.propertySettingsStatus,
      houseRules: data.houseRules,
      houseRulesStatus: data.houseRulesStatus,
    })
    const actions = buildOverviewActions({ readinessItems: readiness, insights: data.insights, plan })
    return { guest, readiness, actions }
  }, [data.overview, data.propertySettings, data.propertySettingsStatus, data.houseRules, data.houseRulesStatus, data.insights, property?.name, plan])

  if (data.loading) {
    return (
      <div className="mx-auto max-w-[1200px] px-4 pb-16 pt-6 md:px-7">
        <div className="mb-[18px] grid grid-cols-1 gap-[18px] lg:grid-cols-[1.6fr_1fr]">
          <div className="h-[220px] animate-pulse rounded-card bg-line-soft" />
          <div className="h-[220px] animate-pulse rounded-card bg-line-soft" />
        </div>
        <div className="grid grid-cols-2 gap-3.5 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-[120px] animate-pulse rounded-card bg-line-soft" />)}
        </div>
      </div>
    )
  }

  if (data.error || !data.overview || !model) {
    return (
      <div className="mx-auto max-w-[1200px] px-4 pb-16 pt-6 md:px-7">
        <div className="flex flex-col items-center justify-center rounded-card border border-border bg-card px-6 py-16 text-center shadow-sm">
          <AlertTriangle className="h-8 w-8 text-amber-strong" strokeWidth={1.6} />
          <p className="mt-3 text-[14px] font-semibold text-foreground">Overview unavailable</p>
          <p className="mt-1 text-[12.5px] text-muted-foreground">{data.error || "We couldn't load this property's overview."}</p>
          <button type="button" onClick={data.refresh} className="mt-4 rounded-[9px] bg-forest px-4 py-2 text-[12.5px] font-semibold text-white hover:opacity-90">
            Try again
          </button>
        </div>
      </div>
    )
  }

  const { guest, readiness, actions } = model
  const overview = data.overview
  const ps = data.performanceSummary
  const trend = ps?.trend || []
  const booking = overview.nextBooking

  // Reputation
  const teaser = overview.reviewTeaser
  const repScore = data.reviewIntelligence?.reputation_score ?? teaser?.reputation_score ?? null
  const repRating = teaser?.average_rating ?? null
  const repCount = teaser?.total_reviews_count ?? data.reviewIntelligence?.review_count ?? null
  const repTrend = data.reviewIntelligence?.analysis?.sentiment_trend
  const repNarrative = buildReputationNarrative(data.reviewIntelligence, teaser)

  // KPIs
  const kpis: { label: string; value: string; delta: number | null | undefined; series: number[]; note: string }[] = []
  if (ps?.kpis) {
    kpis.push({ label: "Revenue", value: fmtKpi(ps.kpis.revenue, "currency"), delta: ps.kpis.revenue?.delta, series: trend.map((t) => t.revenue), note: "vs previous period" })
    kpis.push({ label: "Occupancy", value: fmtKpi(ps.kpis.occupancy, "percent"), delta: ps.kpis.occupancy?.delta, series: trend.map((t) => t.occupancy), note: "nights booked" })
    kpis.push({ label: "RevPAR", value: fmtKpi(ps.kpis.revpar, "currency"), delta: ps.kpis.revpar?.delta, series: trend.map((t) => t.revpar), note: "per available night" })
  }
  if (repScore != null) {
    kpis.push({ label: "Reputation", value: String(Math.round(repScore)), delta: null, series: [], note: "blended, all channels" })
  }

  // Timeline — the checkout slot matches BOTH checkout stage keys so the
  // current-phase highlight survives checkout eve (stageKey "checkout_evening").
  const phaseOrder: { keys: string[]; data?: { label: string; summary: string } }[] = [
    { keys: ["before_checkin"], data: overview.stayPhases?.before_checkin },
    { keys: ["arrival_day"], data: overview.stayPhases?.arrival_day },
    { keys: ["during_stay"], data: overview.stayPhases?.during_stay },
    { keys: ["checkout_morning", "checkout_evening"], data: overview.stayPhases?.checkout_morning || overview.stayPhases?.checkout_evening },
  ]
  const timeline = phaseOrder.filter((p) => p.data)

  const activity = (overview.recentActivity || []).slice(0, 5)

  const repRingScore = repScore != null ? Math.max(0, Math.min(100, repScore)) : 0
  const repCirc = 2 * Math.PI * 31
  const arrivalWeekday = booking?.arrival_date
    ? new Date(booking.arrival_date).toLocaleDateString(undefined, { weekday: "long" })
    : ""
  const heroTitle = guest.hasBooking
    ? guest.stageKey === "before_checkin" && guest.guestName
      ? `${guest.guestName} arrives ${arrivalWeekday}`
      : `${guest.guestName || "Your guest"} · ${guest.stageKicker}`
    : property?.name || "Your property"

  return (
    <div className="mx-auto max-w-[1200px] px-4 pb-16 pt-6 md:px-7">
      {/* HERO + GUEST PREVIEW */}
      <section className="mb-[18px] grid grid-cols-1 gap-[18px] lg:grid-cols-[1.6fr_1fr]">
        <div
          className="relative overflow-hidden rounded-card px-[30px] py-7 text-[#EAF3EE] shadow-sm"
          style={{ background: "radial-gradient(135% 150% at 100% 0%,#1c4c3b 0%,var(--sf-forest) 58%)" }}
        >
          <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(58% 78% at 92% 112%,rgba(30,200,148,.22),transparent 70%)" }} />
          <div className="relative">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-white/[0.16] bg-white/10 px-3 py-1.5 text-[11.5px] font-semibold">
              <span className="h-[7px] w-[7px] rounded-full bg-[#3ee0a0]" style={{ boxShadow: "0 0 0 3px rgba(62,224,160,.25)" }} />
              {guest.stageKicker}
            </span>
            <h2 className="mt-[17px] font-display text-[31px] font-bold leading-[1.07] tracking-[-0.025em]">{heroTitle}</h2>
            <p className="mt-2.5 max-w-[48ch] text-[14.5px] font-normal leading-[1.55] text-[#B9D0C6]">{guest.summary}</p>
            {guest.meta.length > 0 && (
              <div className="mb-[22px] mt-[18px] flex flex-wrap gap-2">
                {guest.meta.map((m, i) => (
                  <span key={i} className="rounded-full bg-white/[0.08] px-[11px] py-[5px] text-[12px] font-medium text-[#D6E6DE]">{m}</span>
                ))}
              </div>
            )}
            <div className="mt-[18px] flex gap-2.5">
              <button
                type="button"
                onClick={() => navigate(surfaceRoute("guidebook", propertyId))}
                className="inline-flex items-center gap-2 rounded-[11px] bg-white px-[18px] py-[11px] text-[13.5px] font-semibold text-forest transition-opacity hover:opacity-90"
                style={{ fontWeight: 650 }}
              >
                Open guidebook
                <ArrowRight className="h-4 w-4" strokeWidth={2} />
              </button>
              <button
                type="button"
                onClick={() => navigate(`/properties/${propertyId}/settings`)}
                className="rounded-[11px] border border-white/20 bg-white/[0.06] px-[18px] py-[11px] text-[13.5px] font-semibold text-[#EAF3EE] transition-colors hover:bg-white/[0.16]"
              >
                Booking details
              </button>
            </div>
          </div>
        </div>

        {/* What guests see — real backend display preview */}
        <div className="flex flex-col rounded-card border border-border bg-card p-4 shadow-sm">
          <div className="mb-3 flex items-center justify-between gap-2">
            <span className="text-[12px] font-semibold text-ink-2" style={{ fontWeight: 650 }}>What guests see</span>
            <span className="inline-flex flex-none items-center gap-1.5 rounded-full bg-brand-soft px-2 py-[3px] text-[10.5px] font-semibold text-brand-strong">
              <span className="h-1.5 w-1.5 rounded-full bg-brand" />
              Live
            </span>
          </div>
          <div className="flex-1 [&_.ovx-preview]:m-0 [&_.ovx-preview]:border-0 [&_.ovx-preview]:p-0 [&_.ovx-preview]:shadow-none [&_.ovx-preview]:bg-transparent">
            <OverviewDisplayPreview
              propertyId={propertyId}
              title={guest.hasBooking ? `Welcome, ${guest.guestName || "guest"}` : property?.name || "Welcome"}
              summary={guest.summary}
              isDemo={isDemo}
            />
          </div>
        </div>
      </section>

      {/* COMMERCIAL SNAPSHOT */}
      {kpis.length > 0 && (
        <section className="mb-[18px]">
          <div className="mb-[11px] flex items-baseline justify-between">
            <span className={eyebrow}>Commercial snapshot · this month</span>
            <button type="button" onClick={() => navigate(`/properties/${propertyId}/performance`)} className="inline-flex items-center gap-1 text-[12px] font-semibold text-ink-2 hover:text-foreground">
              Open Revenue
              <ChevronRight className="h-3 w-3" strokeWidth={2} />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3.5 lg:grid-cols-4">
            {kpis.map((k) => (
              <article key={k.label} className="rounded-card border border-border bg-card px-4 pb-3 pt-4 shadow-sm">
                <div className="flex items-start justify-between">
                  <span className="text-[12px] font-semibold text-muted-foreground">{k.label}</span>
                  {k.delta != null && (
                    <span className={cn("inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[11px] font-semibold", k.delta >= 0 ? "bg-brand-soft text-brand-strong" : "bg-rose-soft text-rose-strong")}>
                      {k.delta >= 0 ? <TrendingUp className="h-2.5 w-2.5" strokeWidth={2.4} /> : <TrendingDown className="h-2.5 w-2.5" strokeWidth={2.4} />}
                      {k.delta >= 0 ? "+" : ""}{k.delta.toFixed(1)}%
                    </span>
                  )}
                </div>
                <div className="mt-2.5 flex items-end justify-between gap-2">
                  <strong className="font-display text-[26px] font-bold tracking-[-0.02em] text-foreground">{k.value}</strong>
                  <Sparkline series={k.series} />
                </div>
                <p className="mt-2 text-[11.5px] font-medium text-muted-foreground">{k.note}</p>
              </article>
            ))}
          </div>
        </section>
      )}

      {/* ACTION RAIL + SIDE */}
      <section className="mb-[18px] grid grid-cols-1 gap-[18px] lg:grid-cols-[1.6fr_1fr]">
        <div className="rounded-card border border-border bg-card p-5 shadow-sm">
          <div className="mb-1.5 flex items-start justify-between">
            <div>
              <div className="mb-1.5 inline-flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-[0.07em] text-brand-strong">
                <Sparkles className="h-3 w-3 fill-current" strokeWidth={0} />
                Prioritised by Stayframe AI
              </div>
              <h3 className="font-display text-[18px] font-bold tracking-[-0.02em] text-foreground">Needs your attention</h3>
            </div>
            <span className="pt-1 text-[12px] font-semibold text-muted-foreground">
              {actions.length === 0 ? "All clear" : `${actions.length} open`}
            </span>
          </div>
          <p className="mb-4 text-[13px] text-muted-foreground">Open the surface to act — Stayframe keeps the context.</p>
          <div className="flex flex-col gap-[11px]">
            {actions.length === 0 ? (
              <div className="flex items-center gap-3.5 rounded-2xl border border-dashed border-line-2 p-[22px] text-ink-2">
                <span className="flex h-[34px] w-[34px] flex-none items-center justify-center rounded-[10px] bg-brand-soft">
                  <Check className="h-[18px] w-[18px] text-brand" strokeWidth={2.2} />
                </span>
                <div>
                  <p className="text-[14px] font-semibold text-foreground" style={{ fontWeight: 650 }}>All clear for now.</p>
                  <span className="text-[12.5px] text-muted-foreground">Use this window to refine the guidebook or pricing.</span>
                </div>
              </div>
            ) : (
              actions.map((a, i) => {
                const tone = ACTION_TONE[a.tone]
                return (
                  <article key={a.key} className="flex gap-[15px] rounded-2xl border border-border bg-card p-4">
                    <div className={cn("flex h-[34px] w-[34px] flex-none items-center justify-center rounded-[10px] font-mono text-[13px] font-bold", tone.chip, tone.text)}>
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <div className="min-w-0 flex-1">
                      <span className={cn("mb-1 inline-flex items-center gap-1.5 text-[11px] font-semibold", tone.text)}>
                        <span className={cn("h-1.5 w-1.5 rounded-full", tone.text.replace("text-", "bg-"))} />
                        {a.sourceLabel}
                      </span>
                      <h4 className="text-[14.5px] font-semibold text-foreground" style={{ fontWeight: 650 }}>{a.title}</h4>
                      <p className="mt-1 text-[12.5px] leading-[1.5] text-ink-2">{a.detail}</p>
                      <p className={cn("mt-1.5 text-[12px] leading-[1.45]", tone.text)}>{a.consequence}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => navigate(surfaceRoute(a.surface, propertyId))}
                      className="h-fit flex-none whitespace-nowrap rounded-[9px] border border-border bg-card px-3 py-2 text-[12.5px] font-semibold text-foreground transition-all hover:border-foreground hover:bg-foreground hover:text-white"
                    >
                      {a.ctaLabel}
                    </button>
                  </article>
                )
              })
            )}
          </div>
        </div>

        <div className="flex flex-col gap-[18px]">
          {/* Reputation mini */}
          <div className="rounded-card border border-border bg-card p-5 shadow-sm">
            <div className="mb-4 flex items-center justify-between">
              <span className={eyebrow}>Reputation</span>
              <button type="button" onClick={() => navigate(`/properties/${propertyId}/reviews`)} className="inline-flex items-center gap-1 text-[12px] font-semibold text-ink-2 hover:text-foreground">
                Reviews
                <ChevronRight className="h-3 w-3" strokeWidth={2} />
              </button>
            </div>
            {repScore != null ? (
              <>
                <div className="flex items-center gap-4">
                  <div className="relative h-[74px] w-[74px] flex-none">
                    <svg width="74" height="74" viewBox="0 0 74 74">
                      <circle cx="37" cy="37" r="31" fill="none" stroke="var(--sf-line)" strokeWidth="7" />
                      <circle cx="37" cy="37" r="31" fill="none" stroke="var(--sf-brand)" strokeWidth="7" strokeLinecap="round" strokeDasharray={repCirc} strokeDashoffset={repCirc * (1 - repRingScore / 100)} transform="rotate(-90 37 37)" />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <strong className="font-display text-[22px] font-bold leading-none text-foreground">{Math.round(repScore)}</strong>
                    </div>
                  </div>
                  <div>
                    <div className="text-[15px] font-bold text-foreground">{repScore >= 90 ? "Excellent" : repScore >= 75 ? "Strong" : "Building"}</div>
                    {repTrend && (
                      <div className={cn("mt-0.5 inline-flex items-center gap-1 text-[12px] font-semibold", repTrend === "softening" ? "text-amber-strong" : "text-brand-strong")}>
                        {repTrend === "softening" ? <TrendingDown className="h-3 w-3" strokeWidth={2.4} /> : <TrendingUp className="h-3 w-3" strokeWidth={2.4} />}
                        {repTrend === "improving" ? "Improving" : repTrend === "softening" ? "Softening" : "Steady"}
                      </div>
                    )}
                    <div className="mt-2 flex items-center gap-2.5">
                      {repRating != null && (
                        <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-ink-2">
                          <Star className="h-3 w-3 fill-gold text-gold" strokeWidth={0} />
                          {repRating.toFixed(1)}
                        </span>
                      )}
                      {repCount != null && <span className="text-[12px] font-medium text-muted-foreground">{repCount} reviews</span>}
                    </div>
                  </div>
                </div>
                <p className="mt-3.5 border-t border-border pt-3 text-[12.5px] leading-[1.5] text-ink-2">{repNarrative}</p>
              </>
            ) : (
              <p className="py-4 text-[12.5px] text-muted-foreground">Reputation appears once reviews are synced.</p>
            )}
          </div>

          {/* Stay readiness */}
          <div className="rounded-card border border-border bg-card p-5 shadow-sm">
            <div className={cn(eyebrow, "mb-3")}>Stay readiness</div>
            <div className="flex flex-col">
              {readiness.map((item) => {
                const t = READINESS_TONE[item.tone]
                return (
                  <div key={item.key} className="flex items-center gap-2.5 border-t border-line-soft py-[9px] first:border-t-0">
                    <span className={cn("flex h-6 w-6 flex-none items-center justify-center rounded-[7px]", t.bg)}>
                      {t.ok ? <Check className={cn("h-3 w-3", t.ic)} strokeWidth={2.6} /> : <AlertTriangle className={cn("h-3 w-3", t.ic)} strokeWidth={2.4} />}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="text-[12.5px] font-semibold text-foreground">{item.label}</div>
                      <div className="truncate text-[11.5px] text-muted-foreground">{item.note || item.detail}</div>
                    </div>
                    <span className={cn("text-[11px] font-semibold", t.ic)}>{t.state}</span>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </section>

      {/* TIMELINE + ACTIVITY */}
      <section className="grid grid-cols-1 gap-[18px] lg:grid-cols-[1.6fr_1fr]">
        <div className="rounded-card border border-border bg-card p-5 shadow-sm">
          <div className={cn(eyebrow, "mb-1")}>Stay orchestration</div>
          <h3 className="mb-[18px] font-display text-[17px] font-bold tracking-[-0.02em] text-foreground">
            {guest.hasBooking ? `How ${guest.guestName || "the"}${guest.guestName ? "’s" : " next"} stay will flow` : "How the next stay will flow"}
          </h3>
          {timeline.length > 0 ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {timeline.map((p) => {
                const current = p.keys.includes(guest.stageKey)
                return (
                  <div key={p.keys[0]} className="relative pt-[18px]">
                    <div className="absolute left-0 right-0 top-1 h-0.5 bg-line" />
                    <div className={cn("absolute left-0 top-0 h-2.5 w-2.5 rounded-full border-2 border-card", current ? "bg-brand" : "bg-line-2")} style={current ? { boxShadow: "0 0 0 2px var(--sf-brand)" } : undefined} />
                    <div className={cn("text-[13px] font-semibold", current ? "text-foreground" : "text-ink-2")} style={{ fontWeight: 650 }}>{p.data!.label}</div>
                    <p className="mt-1.5 text-[11.5px] leading-[1.45] text-ink-2">{p.data!.summary}</p>
                  </div>
                )
              })}
            </div>
          ) : (
            <p className="text-[12.5px] text-muted-foreground">The stay timeline appears once a booking is on the calendar.</p>
          )}
        </div>
        <div className="rounded-card border border-border bg-card p-5 shadow-sm">
          <div className={cn(eyebrow, "mb-3")}>Recent activity</div>
          {activity.length > 0 ? (
            <div className="flex flex-col">
              {activity.map((e) => (
                <div key={e.id} className="flex items-start gap-2.5 border-t border-line-soft py-2.5 first:border-t-0">
                  <span className="mt-[5px] h-2 w-2 flex-none rounded-full bg-brand" />
                  <div className="min-w-0 flex-1">
                    <div className="text-[12.5px] font-medium leading-[1.35] text-foreground">{e.label}</div>
                    <div className="mt-0.5 text-[11px] font-medium text-muted-foreground">
                      {(e.actor_email ? `${e.actor_email} · ` : "") + formatRelativeTime(e.timestamp)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[12.5px] text-muted-foreground">No recent activity yet.</p>
          )}
        </div>
      </section>

      <div className="mt-6">
        <FeedbackTrigger pageSlug="overview" />
      </div>
    </div>
  )
}
